import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  runTransaction,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Bank, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

const bankCreateSchema = z.object({
  name: z.string().min(1, '銀行名は必須です'),
  branch: z.string().min(1, '支店名は必須です'),
  accountType: z.enum(['普通', '当座', '貯蓄'], { required_error: '口座種別は必須です' }),
  accountNumber: z.string().min(1, '口座番号は必須です'),
  accountName: z.string().min(1, '口座名義は必須です'),
  isDefault: z.boolean().default(false),
});

export type BankCreateInput = z.infer<typeof bankCreateSchema>;
export type BankUpdateInput = Partial<BankCreateInput>;

const COL = 'banks';

export async function fetchBanks(): Promise<Bank[]> {
  const q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('name')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Bank);
}

export async function fetchBank(id: ID): Promise<Bank | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<Bank>(snap);
}

/**
 * 帳票の振込先として使う既定の銀行口座を取得する。
 * デフォルト指定（isDefault）があればそれを、無ければ先頭の口座を返す。
 * 請求書に個別の振込先（bankId）が無い場合のフォールバックに使う。
 */
export async function fetchDefaultBank(): Promise<Bank | null> {
  const banks = await fetchBanks();
  return banks.find((b) => b.isDefault) ?? banks[0] ?? null;
}

export async function createBank(
  data: BankCreateInput,
  userId: ID
): Promise<Bank> {
  const validated = parseOrThrow(bankCreateSchema, data);
  const payload = { ...validated, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as Bank;
  await writeAuditLog({
    entityType: 'bank',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return created;
}

export async function updateBank(
  id: ID,
  data: BankUpdateInput,
  userId: ID
): Promise<Bank> {
  const validated = bankCreateSchema.partial().parse(data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('銀行口座が見つかりません。');
  const before = snap.data();
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'bank',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: validated,
  });
  return { id, ...before, ...patch } as unknown as Bank;
}

export async function deleteBank(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('銀行口座が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'bank',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}

/**
 * 指定した銀行口座をデフォルトに設定し、他のデフォルトを解除する。
 *
 * 実装上の注意:
 * Firestore はトランザクション内でのコレクションクエリ (getDocs) をサポートしないため、
 * 既存デフォルト口座の取得はトランザクション外で事前に行う。
 * その後、取得した ID リストを使ってトランザクション内で個別ドキュメントを更新することで
 * アトミックな書き込みを保証する。
 * 運用規模 (100名・低更新頻度) においてレースコンディションのリスクは実用上無視できる。
 */
export async function setDefaultBank(id: ID, userId: ID): Promise<void> {
  // ① トランザクション外で現在のデフォルト銀行を全件取得（read-only）
  const currentDefaultSnap = await getDocs(
    query(collection(db, COL), where('isDefault', '==', true), where('isDeleted', '==', false))
  );

  // ② トランザクション内で対象をデフォルトに設定し、旧デフォルトを解除
  await runTransaction(db, async (tx) => {
    const targetRef = doc(db, COL, id);
    const targetSnap = await tx.get(targetRef);
    if (!targetSnap.exists()) throw new Error('銀行口座が見つかりません。');

    for (const d of currentDefaultSnap.docs) {
      if (d.id !== id) {
        tx.update(doc(db, COL, d.id), { isDefault: false, ...buildUpdateFields(userId) });
      }
    }
    tx.update(targetRef, { isDefault: true, ...buildUpdateFields(userId) });
  });

  await writeAuditLog({
    entityType: 'bank',
    entityId: id,
    action: 'update',
    userId,
    after: { isDefault: true },
  });
}
